import { API_BASE_URL, ApiError } from './client';

/**
 * A job somebody is down for — TROOP-162, TROOP-163, TROOP-165.
 *
 * **A schedule row is a duty with a time on it**, which is why there is one shape here and not
 * two. `startsAt` is the instant it happens at, computed server-side from the event's start plus
 * the elapsed offset; a row with no time has both null and that is ordinary — half a program is a
 * running order and half is a list of things to remember.
 */
export interface EventDuty {
  id: string;
  label: string;
  assignedToPersonId: string | null;
  assignedToName: string | null;
  assignedToSubgroupId: string | null;
  assignedToSubgroupName: string | null;
  sortOrder: number;
  /** Minutes from the event's start. Negative is a crew setting up before the doors open. */
  startOffsetMinutes: number | null;
  durationMinutes: number | null;
  /** Never derived here — see `EventSummary.month` for why the server does this arithmetic. */
  startsAt: string | null;
  isDone: boolean;
  completedAt: string | null;
  completedByName: string | null;
  /** Theirs, their child's, or a group either is in. What the Yours tab filters on. */
  isYours: boolean;
  /**
   * Whether this reader may tick it. **Per row, never per response** — a parent may tick their own
   * child's line and not the one above it, so a frozen constant would fail silent rather than
   * closed (TROOP-73).
   */
  canCheck: boolean;
}

export interface EventDuties {
  duties: EventDuty[];
  /** Add, rename, assign, schedule, remove. Position only — a youth Patrol Leader has this. */
  canWrite: boolean;
  /** The unit's own word for a group. Rendered, never assumed (rule 16). */
  subgroupLabelSingular: string;
  subgroupLabelPlural: string;
}

/** `unitId` absent picks the guardian's door — the same two-door switch every other client uses. */
const base = (unitId: string | undefined, eventId: string) =>
  unitId
    ? `${API_BASE_URL}/units/${unitId}/events/${eventId}/duties`
    : `${API_BASE_URL}/family/events/${eventId}/duties`;

async function call<T>(url: string, init?: RequestInit): Promise<T> {
  const response = await fetch(url, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...init?.headers },
    ...init,
  });

  if (!response.ok) {
    throw new ApiError(response.status, `${init?.method ?? 'GET'} ${url} failed`);
  }

  return response.status === 204 ? (undefined as T) : ((await response.json()) as T);
}

export const dutiesApi = {
  list: (unitId: string | undefined, eventId: string) =>
    call<EventDuties>(`${base(unitId, eventId)}/`),

  add: (unitId: string, eventId: string, label: string) =>
    call<{ dutyId: string }>(`${base(unitId, eventId)}/`, {
      method: 'POST',
      body: JSON.stringify({ label }),
    }),

  /**
   * Absent fields are left alone, which is why clearing needs its own flag: two nullable ids
   * cannot say the difference between "not stated" and "nobody".
   */
  update: (
    unitId: string,
    eventId: string,
    dutyId: string,
    patch: {
      label?: string;
      assignedToPersonId?: string | null;
      assignedToSubgroupId?: string | null;
      unassign?: boolean;
      startOffsetMinutes?: number | null;
      durationMinutes?: number | null;
      clearSchedule?: boolean;
    },
  ) =>
    call<void>(`${base(unitId, eventId)}/${dutyId}`, {
      method: 'PUT',
      body: JSON.stringify(patch),
    }),

  reorder: (unitId: string, eventId: string, order: string[]) =>
    call<void>(`${base(unitId, eventId)}/order`, {
      method: 'POST',
      body: JSON.stringify({ order }),
    }),

  remove: (unitId: string, eventId: string, dutyId: string) =>
    call<void>(`${base(unitId, eventId)}/${dutyId}`, { method: 'DELETE' }),

  /**
   * Ticking, through whichever door the reader came in. A guardian has no `/units/` route at all
   * (they hold no membership), so this is not a lesser version of the member's call — it is the
   * same rule reached the only way they can reach it.
   */
  setDone: (unitId: string | undefined, eventId: string, dutyId: string, done: boolean) =>
    call<void>(
      unitId
        ? `${base(unitId, eventId)}/${dutyId}/done`
        : `${API_BASE_URL}/family/events/${eventId}/duties/${dutyId}/done`,
      { method: 'POST', body: JSON.stringify({ done }) },
    ),
};
